import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router'
import { observer } from 'mobx-react-lite'

// evertel
import {
    PageBody, Row, Col, Button, Icon,
    Text, Breadcrumb, InfoBox, useUI
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { UserController } from '@evertel/blue-user'
import {
    CloneSchemaController,
    GlobalSchemaCategoriesController
} from '@evertel/document'
import { APIDataDocumentSchema, APIDataDocumentSchemaCategory } from '@evertel/types'
import { DocumentTemplateCard } from '../elements'
import { CategoryList, BrowseCategory, categoryTabId } from './CategoryList'
import { TemplateGrid, TemplateGridItemProps } from './TemplateGrid'
import { SchemaPreviewModal } from '../schema-templates/SchemaPreviewModal'

const templatePanelId = 'catalog-templates-panel'

const DocumentTemplatesCatalog: React.FC = observer(() => {

    const navigate = useNavigate()
    const location = useLocation()
    const { target } = useParams()
    const { addToast } = useUI()

    const sessionService = useService(SessionService, [])
    const userController = useService(UserController, [])
    const globalSchemaCategoriesController = useService(GlobalSchemaCategoriesController, [])
    const cloneController = useService(CloneSchemaController, [])

    // selection and filter live in the URL so browser back (e.g. from the clone/edit page)
    // lands on the same category the user left from
    const [searchParams, setSearchParams] = useSearchParams()

    const updateParams = (updates: Record<string, string | null>) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            Object.entries(updates).forEach(([key, value]) => {
                if (value) next.set(key, value)
                else next.delete(key)
            })
            return next
        }, { replace: true })
    }

    const onSelectKey = (key: string) => updateParams({ category: key })

    // the search box is driven by local state, not the URL: setSearchParams is an async
    // navigation, so a controlled input bound to it re-renders with stale text between
    // keystrokes and drops characters. The URL is seeded once on mount and then written
    // one-way (debounced) so it's only ever read back when the page is re-entered.
    const [searchValue, setSearchValue] = useState(() => searchParams.get('q') ?? '')

    useEffect(() => {
        const timeout = setTimeout(() => updateParams({ q: searchValue.trim() || null }), 250)
        return () => clearTimeout(timeout)
    }, [searchValue])

    const [selectedSchema, setSelectedSchema] = useState<APIDataDocumentSchema>()
    const [showPreviewModal, setShowPreviewModal] = useState(false)
    const [isCloning, setIsCloning] = useState(false)

    // editing global templates is an admin-only flag layered on top of the same
    // browsing screen — not a distinct experience
    const editGlobalTemplates = location.state?.isAdmin || target === 'global'
    const isAdmin = editGlobalTemplates && !!userController.user?.isAdmin

    useEffect(() => {
        userController.init(sessionService.currentUserId)
    }, [sessionService.currentUserId])

    useEffect(() => {
        // categories and their templates come back in a single request
        globalSchemaCategoriesController.init(true)
    }, [])

    const schemasForCategory = (categoryId: number): APIDataDocumentSchema[] => {
        const schemas = globalSchemaCategoriesController.schemasForCategory(categoryId)
        return isAdmin ? schemas : schemas.filter(s => s.isEnabled !== false)
    }

    // while searching, each category only counts (and only lists) its matching templates,
    // so the list doubles as the "which category is this result in" answer
    const searchTerm = searchValue.trim().toLowerCase()
    const isSearching = !!searchTerm
    const matchesSearch = (schema: APIDataDocumentSchema) =>
        !isSearching || !!schema.name?.toLowerCase().includes(searchTerm)

    // disabled global templates stay listed in their assigned categories (admins only) and
    // are additionally collected here so they can be found in one place
    const disabledSchemas = isAdmin
        ? globalSchemaCategoriesController.allSchemas.filter(s => s.isEnabled === false)
        : []

    const allCategories: BrowseCategory[] = globalSchemaCategoriesController.categories.map((category: APIDataDocumentSchemaCategory) => ({
        key: `category-${category.id}`,
        label: category.name as string,
        count: schemasForCategory(category.id as number).filter(matchesSearch).length
    }))
    if (isAdmin) {
        allCategories.push({ key: 'disabled', label: 'Disabled', count: disabledSchemas.filter(matchesSearch).length })
    }

    // hide categories with no matches while searching. Unlike MyTemplates there is no "All"
    // anchor here, so if nothing matches anywhere keep the full list visible rather than
    // collapsing the sidebar to nothing
    const matchingCategories = allCategories.filter(c => c.count > 0)
    const categories = isSearching && matchingCategories.length
        ? matchingCategories
        : allCategories

    // no explicit selection yet: default to the first category. While searching, fall
    // back to the first category that has matches if the chosen one has none
    const requestedKey = searchParams.get('category') ?? categories[0]?.key ?? ''
    const selectedKey = categories.some(c => c.key === requestedKey)
        ? requestedKey
        : (categories[0]?.key ?? '')

    const schemasForKey = (key: string): APIDataDocumentSchema[] => {
        if (!key) return []
        if (key === 'disabled') return disabledSchemas

        return schemasForCategory(parseInt(key.replace('category-', '')))
    }

    const visibleSchemas = schemasForKey(selectedKey).filter(matchesSearch)

    const selectedLabel = categories.find(c => c.key === selectedKey)?.label || ''
    const panelTitle = isSearching
        ? `Search results in ${selectedLabel}`
        : selectedLabel

    const previewSchema = (schema: APIDataDocumentSchema) => {
        setSelectedSchema(schema)
        setShowPreviewModal(true)
    }

    const onCloneSchema = async (schema: APIDataDocumentSchema) => {
        if (!schema?.id || !sessionService?.selectedDepartmentId) return

        setIsCloning(true)
        try {
            const newSchema = await cloneController.cloneSchema(schema.id, sessionService.selectedDepartmentId, schema.name + ' (copy)')

            setShowPreviewModal(false)
            navigate(`/everdocs/schema/edit/${newSchema.id}`)
        } catch (error: any) {
            setShowPreviewModal(false)
            addToast({
                color: 'danger',
                title: 'Clone Unsuccessful',
                message: error.message || error
            })
        }
        setIsCloning(false)
    }

    // disabled templates are only ever in the list for admins; they read as muted but stay
    // clickable so the admin can still preview and clone them
    const renderCard = (schema: APIDataDocumentSchema, itemProps: TemplateGridItemProps) => (
        <DocumentTemplateCard
            key={schema.id}
            schema={schema}
            disabled={schema.isEnabled === false}
            onClick={() => previewSchema(schema)}
            {...itemProps}
        />
    )

    return (
        <PageBody
            flush
            className="documents-page">
            <div className="px-4 pt-4">
                {isAdmin &&
                    <InfoBox
                        color="warning"
                        className="mb-3">
                        <Text>You are editing Global Templates!</Text>
                    </InfoBox>
                }
                <Breadcrumb
                    items={[
                        { label: 'New Template', onClick: () => navigate('/everdocs/schema/new') },
                        { label: 'Evertel Catalog', active: true }
                    ]}
                />
                <Row
                    align="between"
                    valign="center">
                    <Text
                        tag="h3"
                        className="mb-0">
                        Evertel Catalog
                    </Text>
                    {isAdmin &&
                        <Button
                            color="primary"
                            onClick={() => navigate('/everdocs/schema/add/global', {
                                state: { isAdmin: true, from: location.pathname }
                            })}>
                            <Icon
                                name="plus"
                                className="me-2"
                            />
                            Create Global Template
                        </Button>
                    }
                </Row>
                <Text
                    tag="p"
                    color="muted">
                    Find a template to edit and publish for your agency.
                </Text>
            </div>
            <Row
                valign="top"
                className="px-4 pb-4">
                <Col
                    valign="top"
                    className="category-list-column pe-3">
                    <CategoryList
                        categories={categories}
                        selectedKey={selectedKey}
                        onSelect={onSelectKey}
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        panelId={templatePanelId}
                    />
                </Col>
                <Col valign="top">
                    <TemplateGrid
                        id={templatePanelId}
                        labelledBy={categoryTabId(selectedKey)}
                        title={panelTitle}
                        schemas={visibleSchemas}
                        renderCard={renderCard}
                        emptyMessage={isSearching ? `No templates match "${searchValue.trim()}".` : undefined}
                    />
                </Col>
            </Row>
            <SchemaPreviewModal
                visible={showPreviewModal}
                isLoading={isCloning}
                schema={selectedSchema}
                onClose={() => setShowPreviewModal(false)}
                onUse={onCloneSchema}
            />
        </PageBody>
    )
})

export { DocumentTemplatesCatalog }
